import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useDaoConfig } from '@/hooks/useDaoConfig';

import { animateElement, type AnimationType } from './../animations';
import type { WelcomeStepRef } from './types';

export const WelcomeStep4 = forwardRef<WelcomeStepRef>((_, ref) => {
  const { clientRole, workerRole } = useDaoConfig();
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const elements = containerRef.current?.querySelectorAll<HTMLElement>('[data-animate-in]');

    elements?.forEach((element) => {
      const animation = element.dataset.animateIn as AnimationType | undefined;

      if (animation) {
        animateElement(element, animation).catch(() => {});
      }
    });
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      animateOut: async () => {
        const elements = containerRef.current?.querySelectorAll<HTMLElement>('[data-animate-out]');

        if (!elements) return;

        await Promise.all(
          Array.from(elements).map((element) => {
            const animation = element.dataset.animateOut as AnimationType | undefined;

            if (!animation) return Promise.resolve();

            return animateElement(element, animation);
          }),
        );
      },
    }),
    [],
  );

  return (
    <div ref={containerRef} className="stepContainer">
      <h1 data-animate-in="textFadeIn" data-animate-out="textFadeOut" className="opacity-0">
        Established project flow, risk free.
      </h1>

      <p
        data-animate-in="textFadeIn"
        data-animate-out="textFadeOut"
        className="opacity-0 max-w-[90ch] text-[--text-dark-secondary] font-normal"
      >
        When a project starts, the {clientRole.toLowerCase()} puts the full payment upfront. It stays locked, and gets
        released to the {workerRole.toLowerCase()} as each milestone is approved. Every step is on record, so
        reputations don't have to be taken on faith.
      </p>

      {/* Image - animations container */}
      <div className="animationsContainer mt-20">
        <div className="relative">
          <div
            data-animate-in="scaleUp4"
            data-animate-out="fadeOut"
            className="h-[167px] translate-y-4 aspect-square rounded-full glassEffect border-0 opacity-0"
          >
            <img
              src="./public/images/onboarding/gradientBorder_circle.svg"
              // alt="gradient border circle"
            />
          </div>

          {/* Blue bubble - top left */}
          <div
            data-animate-in="fadeIn"
            data-animate-out="fadeOut"
            className="
              z-50 absolute opacity-0
              -top-[13%] -left-[13%] -translate-x-1/2 -translate-y-1/2
              h-[37px] aspect-square max-w-[37px]
              rounded-full bg-[--colors-cyan-600]
            "
          ></div>

          {/* Green bubble - bottom right */}
          <div
            data-animate-in="fadeIn"
            data-animate-out="fadeOut"
            className="
              z-50 absolute opacity-0
              top-[113%] left-[113%] -translate-x-1/2 -translate-y-1/2
              h-[37px] aspect-square 
              rounded-full bg-[--colors-emerald-500]
            "
          ></div>
        </div>
      </div>
    </div>
  );
});

WelcomeStep4.displayName = 'WelcomeStep4';
